// Pure formatting for a delivery schedule, shared by the schedule editor and the
// region-wide Scheduled Emails list so the two never describe the same schedule
// in different words.

export interface ScheduleShape {
  frequency: 'daily' | 'weekly' | 'monthly';
  weeklyDays?: number[];
  monthlyDays?: number[];
  timeEST: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** 14, 5 -> "2:05 PM" */
export function formatTimeOfDay(hour: number, minute: number): string {
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return '';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  return `${h}:${minute.toString().padStart(2, '0')} ${ampm}`;
}

/** "07:30" -> "7:30 AM" */
export function formatTimeEst(timeEST: string): string {
  const [hour, minute] = (timeEST || '').split(':').map(Number);
  return formatTimeOfDay(hour, minute);
}

/** "Every Mon, Thu at 9:00 AM EST" */
export function formatFrequencyLabel(schedule: ScheduleShape): string {
  const time = formatTimeEst(schedule.timeEST);

  if (schedule.frequency === 'daily') return `Every day at ${time} EST`;

  if (schedule.frequency === 'weekly') {
    const days = [...(schedule.weeklyDays || [])]
      .sort((a, b) => a - b)
      .map(d => DAY_NAMES[d])
      .filter(Boolean)
      .join(', ');
    // An empty day list is a broken schedule that can never fire - say so
    // rather than rendering "Every  at ...".
    return days ? `Every ${days} at ${time} EST` : `No day selected - never sends`;
  }

  const monthDays = [...(schedule.monthlyDays || [1])].sort((a, b) => a - b).join(', ');
  return `Monthly on day ${monthDays} at ${time} EST`;
}
